import React, { useCallback, useEffect, useState } from 'react';
import { ReportSettingState } from '.';
import { useAuth } from '../../../context';
import {
  ClientCustomCalendarModel,
  ClientSetting,
  ReportType,
} from '../../../models';
import { ReportTypeSetting } from '../../../models/reportTypeSetting';
import { validateReportSettingPITState } from './helpers/validateReportSettingPITState';
import ReportSettingPITInput, {
  ReportSettingPITState,
} from './ReportSettingPITInput';
import { useReportContext } from '../../../context/ReportContext';
import { FeatureFlagContext, useFeatureFlags } from '../../../context/featureFlags';
import { defaultDateFormat, getDateFormatByLocale, isValidDateByFormat, parseLocaleSpecificDateStringToDefaultFormat } from '../../../util/dateHelpers';
import { format } from 'date-fns';

interface ReportSettingPITProps {
  reportType?: ReportType;
  reportSettingState: ReportSettingState;
  reportTypeSetting: ReportTypeSetting;
  isAllowPIT: boolean;
  customCalendarSettings?: ClientCustomCalendarModel;
  onChange?: (updatedValue: ReportSettingState) => void;
}

const initalReportSettingPITState = (
  reportSettingState: ReportSettingState,
  reportType: ReportType,
  clientSetting: ClientSetting,
  customCalendarSettings: ClientCustomCalendarModel,
  dateFormat: string,
  localePreference: string,
  useFeatureFlags: FeatureFlagContext,
  isDateValid: boolean,
  reportScheduledDate?: Date,
): ReportSettingPITState => {
  const {
    pitSwitchValue,
    pitAsOfDate,
    pitErrors,
  }: ReportSettingPITState = reportSettingState;
  let formattedDate = pitAsOfDate;

  if (pitAsOfDate && isDateValid && isValidDateByFormat(pitAsOfDate, defaultDateFormat)) {
      formattedDate = format(new Date(pitAsOfDate), dateFormat);
  }

  const state: ReportSettingPITState = {
    pitSwitchValue,
    pitAsOfDate: formattedDate,
    pitErrors,
  };
  state.pitErrors = validateReportSettingPITState(
    state,
    reportType,
    reportSettingState,
    clientSetting,
    customCalendarSettings,
    dateFormat,
    localePreference,
    useFeatureFlags,
    reportScheduledDate,
  );
  return state;
};

function ReportSettingPIT({
  reportType,
  reportSettingState,
  isAllowPIT,
  customCalendarSettings,
  onChange,
}: ReportSettingPITProps): JSX.Element {
  const [isDateValid, setIsDateValid] = useState(true);
  const { clientSetting, user } = useAuth();
  const { combinedDateTime } = useReportContext();

  const featureFlags = useFeatureFlags();

  const localePreference = user.localePreference;
  const dateFormat = getDateFormatByLocale(localePreference);

  const [pitState, setPitState] = useState<ReportSettingPITState>(
    initalReportSettingPITState(
      reportSettingState,
      reportType,
      clientSetting,
      customCalendarSettings,
      dateFormat,
      localePreference,
      featureFlags,
      isDateValid,
      combinedDateTime,
    ),
  );

  useEffect(() => {
    setPitState(
      initalReportSettingPITState(
        reportSettingState,
        reportType,
        clientSetting,
        customCalendarSettings,
        dateFormat,
        localePreference,
        featureFlags,
        isDateValid,
        combinedDateTime,
      ),
    );
  }, [clientSetting, reportSettingState, reportType, customCalendarSettings, combinedDateTime, dateFormat, localePreference, featureFlags, isDateValid]);

  const getPITErrors = useCallback(
    (state: ReportSettingPITState, reportSettingState: ReportSettingState) => {
      const pitErrors = validateReportSettingPITState(
        state,
        reportType,
        reportSettingState,
        clientSetting,
        customCalendarSettings,
        dateFormat,
        localePreference,
        featureFlags,
        combinedDateTime,
      );
      return pitErrors;
    },
    [reportType, clientSetting, customCalendarSettings, dateFormat, localePreference, featureFlags, combinedDateTime],
  );

  const setNewPitState = useCallback(
    (state: ReportSettingPITState, pitErrors: string[]) => {
      const nextPitState: ReportSettingPITState = {
        ...state,
        pitErrors,
        pitIsInvalid: !!pitErrors?.length,
      };
      setPitState(nextPitState);

      const { pitAsOfDate } = state;
      const isValidAsOfDate = isValidDateByFormat(pitAsOfDate, dateFormat);
      setIsDateValid(isValidAsOfDate);

      let defaultFormatAsOfDate = pitAsOfDate;
      if (isValidAsOfDate) {
        defaultFormatAsOfDate = parseLocaleSpecificDateStringToDefaultFormat(pitAsOfDate, localePreference);
      }

      const onChangePitState = {
        ...nextPitState,
        pitAsOfDate: defaultFormatAsOfDate,
      }
      onChange({ ...reportSettingState, ...onChangePitState });
    },
    [dateFormat, localePreference, onChange, reportSettingState],
  );

  const stateChangeHandler = useCallback(
    (state: ReportSettingPITState) => {
      const pitErrors = getPITErrors(state, reportSettingState);
      setNewPitState(state, pitErrors);
    },
    [getPITErrors, reportSettingState, setNewPitState],
  );

  useEffect(() => {
    // should re-validate PIT info when reportSettingState change
    const pitErrors = getPITErrors(pitState, reportSettingState);
    const hasErrosChanged =
      (pitErrors?.length || 0) !== (pitState?.pitErrors?.length || 0) ||
      pitErrors.some((v, i) => v !== pitState.pitErrors[i]);

    // only raise onChange event if there're some change in pitErrors
    if (hasErrosChanged) {
      setNewPitState(pitState, pitErrors);
    }
  }, [getPITErrors, pitState, reportSettingState, setNewPitState]);

  if (!isAllowPIT) return null;

  return (
    <ReportSettingPITInput onChange={stateChangeHandler} pitState={pitState} />
  );
}

export default ReportSettingPIT;
